import {isCssVariableProperty} from '@packages/systems/core/utils/CssStringUtils';
import type {PropertyOriginType} from './propertyOrigin';

export type StylePanelPropertyName = // Grid
  | 'areas'
  | 'autoColumns'
  | 'autoFlow'
  | 'autoRows'
  | 'columnGap'
  | 'justifyItems'
  | 'columns' // Grid Item
  | 'rows'
  | 'gridItemPosition'
  | 'alignItem'
  | 'gridArea'
  | 'gridColumnStart'
  | 'gridColumnEnd'
  | 'gridRowStart' // Flex Item
  | 'gridRowEnd'
  | 'alignSelf'
  | 'justifySelf'
  | 'basis'
  | 'grow'
  | 'order'
  | 'shrink' // Layout
  | 'flex'
  | 'alignContent'
  | 'alignItems'
  | 'display'
  | 'flexDirection'
  | 'flexWrap'
  | 'justifyContent' // Spacing
  | 'visibility'
  | 'marginBottom'
  | 'marginLeft'
  | 'marginRight'
  | 'marginTop'
  | 'paddingBottom'
  | 'paddingLeft'
  | 'paddingRight' // Position
  | 'paddingTop'
  | 'position'
  | 'positionAnchor'
  | 'positionArea'
  | 'positionVisibility'
  | 'bottom'
  | 'left'
  | 'right'
  | 'top'
  | 'float'
  | 'clear'
  | 'overflow' // Size
  | 'zIndex'
  | 'height'
  | 'maxHeight'
  | 'minHeight'
  | 'width'
  | 'maxWidth'
  | 'minWidth'
  | 'objectFit'
  | 'objectPosition' // Text
  | 'aspectRatio'
  | 'color'
  | 'fontFamily'
  | 'fontStyle'
  | 'fontSize'
  | 'fontVariationSettings'
  | 'fontWeight'
  | 'textShadows'
  | 'textColumns'
  | 'letterSpacing'
  | 'lineHeight'
  | 'textAlign'
  | 'textDecoration'
  | 'textIndent'
  | 'textTransform'
  | 'direction'
  | 'gap'
  | 'ruleColor'
  | 'ruleStyle'
  | 'ruleWidth'
  | 'span'
  | 'columnCount'
  | 'listStyleType'
  | 'whiteSpace'
  | 'textWrapMode'
  | 'whiteSpaceCollapse'
  | 'textWrapStyle'
  | 'hyphens'
  | 'textStrokeWidth'
  | 'textStrokeColor'
  | 'wordBreak' // Box
  | 'overflowWrap'
  | 'backgrounds'
  | 'backgroundColor'
  | 'backgroundClip'
  | 'border'
  | 'borderBottom'
  | 'borderLeft'
  | 'borderRight'
  | 'borderTop'
  | 'borderRadius'
  | 'borderRadiusTopLeft'
  | 'borderRadiusTopRight'
  | 'borderRadiusBottomLeft'
  | 'borderRadiusBottomRight' // Outline
  | 'boxShadows'
  | 'outlineColor'
  | 'outlineOffset'
  | 'outlineStyle' // Effects
  | 'outlineWidth'
  | 'selfPerspective'
  | 'transforms'
  | 'transitions'
  | 'filters'
  | 'backdropFilters'
  | 'opacity'
  | 'cursor'
  | 'pointerEvents'
  | 'mixBlendMode'
  | 'backface'
  | 'transformOrigin'
  | 'childPerspectiveDistance'
  | 'boxSizing'
  | 'childPerspectiveOrigin'
  | 'verticalAlign'
  | 'textOverflow'
  | 'textUnderlineOffset'
  | 'textDecorationThickness'
  | 'textDecorationStyle'
  | 'textDecorationColor'
  | 'textDecorationSkipInk'
  | 'textUnderlinePosition';

export type StyleSystemPath = string;

type BorderCompoundStyleSystemPath = {
  color: StyleSystemPath;
  style: StyleSystemPath;
  width: StyleSystemPath;
};

export type CompoundStyleSystemPath = // border
  | Partial<Record<StylePanelPropertyName, StyleSystemPath>> // borderBottom, borderLeft, borderRight, borderTop
  | {
      bottom: BorderCompoundStyleSystemPath;
      left: BorderCompoundStyleSystemPath;
      right: BorderCompoundStyleSystemPath;
      top: BorderCompoundStyleSystemPath;
    } // flex
  | BorderCompoundStyleSystemPath // textColumns
  | {
      basis: StyleSystemPath;
      grow: StyleSystemPath;
      shrink: StyleSystemPath;
    } // borderRadius
  | {
      gap: StyleSystemPath;
      ruleColor: StyleSystemPath;
      ruleStyle: StyleSystemPath;
      ruleWidth: StyleSystemPath;
      span: StyleSystemPath;
      columnCount: StyleSystemPath;
    } // tranformSettings
  | {
      borderRadiusTopLeft: StyleSystemPath;
      borderRadiusTopRight: StyleSystemPath;
      borderRadiusBottomLeft: StyleSystemPath;
      borderRadiusBottomRight: StyleSystemPath;
      borderRadius: StyleSystemPath;
    }
  | {
      backface: StyleSystemPath;
      transformOrigin: StyleSystemPath;
      childPerspectiveDistance: StyleSystemPath;
      childPerspectiveOrigin: StyleSystemPath;
      selfPerspective: StyleSystemPath;
      transforms: StylePanelPropertyListType;
    };

export const expandPath = (path: string | Array<string>): Array<string> => {
  if (typeof path === 'string') {
    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '(path: string | string[]) => string[]'. | TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '(path: string | string[]) => string[]'.
    return expandPath[path] || (expandPath[path] = path.split('.'));
  }
  return path;
};

export const getPath = (
  propertyName: StylePanelPropertyName | `--${string}`
): StyleSystemPath | CompoundStyleSystemPath =>
  // lookup property name in path table
  isCssVariableProperty(propertyName)
    ? propertyName
    : (paths[propertyName] ?? propertyName);

export type StylePanelPropertyListType =
  | 'backgrounds'
  | 'boxShadows'
  | 'textShadows'
  | 'filters'
  | 'backdropFilters'
  | 'fontVariationSettings'
  | 'transforms'
  | 'transitions';

export type StylePanelPropertyListItemType =
  | 'solid-color-background'
  | 'linear-gradient-background'
  | 'radial-gradient-background'
  | 'image-background';

export const getPropertyListItemPath = (
  path: StyleSystemPath,
  originType: PropertyOriginType,
  index: number
): StyleSystemPath => {
  const updateStream =
    originType === 'local' || originType === 'overridden'
      ? 'local'
      : 'upstream';
  // a property list should always map to a StyleSystemPath
  return `${path}.${updateStream}.value.${index}`;
};

// The Style Panel renames some properties from the legacy stylesystem.
const renames = {
  // filter
  'hue-rotate': 'hueRotate',
  'drop-shadow': 'dropShadow',
  // shadow
  size: 'spread',
  placement: 'position',
  // radial-gradient-background
  extent: 'size',
  // transition
  easing: 'timing',
} as const;

// All StylePanel property names work as legacy stylesystem names except for a few.
export const styleSystemPropertyName = (propertyName: string): string =>
  // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly 'hue-rotate': "hueRotate"; readonly size: "spread"; readonly placement: "position"; readonly extent: "size"; readonly easing: "timing"; }'.
  renames[propertyName] || propertyName;

// Explicit property mapping
// Note: A lot of these are now 1:1 but we will leave that for now.
const paths = {
  // Grid
  areas: 'gridTemplateAreas',
  autoFlow: 'gridAutoFlow',
  autoColumns: 'gridAutoColumns',
  autoRows: 'gridAutoRows',
  columnGap: 'gridColumnGap',
  rowGap: 'gridRowGap',
  columns: 'gridTemplateColumns',
  rows: 'gridTemplateRows',
  justifyItems: 'justifyItems',

  gridItemPosition: {
    gridColumnStart: 'gridColumnStart',
    gridColumnEnd: 'gridColumnEnd',
    gridRowStart: 'gridRowStart',
    gridRowEnd: 'gridRowEnd',
    gridArea: 'gridArea',
  },

  alignItem: {
    alignSelf: 'alignSelf',
    justifySelf: 'justifySelf',
  },

  justifySelf: 'justifySelf',
  gridArea: 'gridArea',
  gridColumnStart: 'gridColumnStart',
  gridColumnEnd: 'gridColumnEnd',
  gridRowStart: 'gridRowStart',
  gridRowEnd: 'gridRowEnd',

  // Flex Item
  alignSelf: 'alignSelf',
  basis: 'flexBasis',
  grow: 'flexGrow',
  order: 'order',
  shrink: 'flexShrink',
  // compound value
  flex: {
    basis: 'flexBasis',
    grow: 'flexGrow',
    shrink: 'flexShrink',
  },

  // Layout
  alignContent: 'alignContent',
  alignItems: 'alignItems',
  display: 'display',
  flexDirection: 'flexDirection',
  flexWrap: 'flexWrap',
  justifyContent: 'justifyContent',
  visibility: 'visibility',

  // Spacing
  marginBottom: 'marginBottom',
  marginLeft: 'marginLeft',
  marginRight: 'marginRight',
  marginTop: 'marginTop',
  paddingBottom: 'paddingBottom',
  paddingLeft: 'paddingLeft',
  paddingRight: 'paddingRight',
  paddingTop: 'paddingTop',
  verticalAlign: 'verticalAlign',

  // Position
  position: 'position',
  bottom: 'bottom',
  left: 'left',
  right: 'right',
  top: 'top',
  float: 'float',
  clear: 'clear',
  overflow: 'overflow',
  zIndex: 'zIndex',
  objectFit: 'objectFit',
  objectPosition: 'objectPosition',

  // Popover Positioning
  positionAnchor: 'positionAnchor',
  positionArea: 'positionArea',
  positionVisibility: 'positionVisibility',

  // Compound value
  positionAndCoordinates: {
    position: 'position',
    bottom: 'bottom',
    left: 'left',
    right: 'right',
    top: 'top',
  },

  // Size
  height: 'height',
  maxHeight: 'maxHeight',
  minHeight: 'minHeight',
  width: 'width',
  maxWidth: 'maxWidth',
  minWidth: 'minWidth',
  aspectRatio: 'aspectRatio',
  boxSizing: 'boxSizing',

  // Text
  color: 'fontColor',
  fontFamily: 'fontFamily',
  fontStyle: 'fontStyle',
  fontSize: 'fontSize',
  fontWeight: 'fontWeight',
  fontVariationSettings: 'fontVariationSettings',
  textShadows: 'textShadows',
  // compound value
  textColumns: {
    gap: 'columnGap',
    ruleColor: 'columnRuleColor',
    ruleStyle: 'columnRuleStyle',
    ruleWidth: 'columnRuleWidth',
    span: 'columnSpan',
    columnCount: 'columnCount',
  },
  gap: 'columnGap',
  ruleColor: 'columnRuleColor',
  ruleStyle: 'columnRuleStyle',
  ruleWidth: 'columnRuleWidth',
  span: 'columnSpan',
  columnCount: 'columnCount',
  letterSpacing: 'letterSpacing',
  lineHeight: 'lineHeight',
  textAlign: 'textAlign',
  textDecoration: 'textDecoration',
  textIndent: 'textIndent',
  textTransform: 'textTransform',
  direction: 'direction',
  listStyleType: 'listStyleType',
  whiteSpace: 'whiteSpace',
  textWrapMode: 'textWrapMode',
  whiteSpaceCollapse: 'whiteSpaceCollapse',
  textWrapStyle: 'textWrapStyle',
  hyphens: 'hyphens',
  textStrokeWidth: 'textStrokeWidth',
  textStrokeColor: 'textStrokeColor',
  wordBreak: 'wordBreak',
  overflowWrap: 'overflowWrap',
  textOverflow: 'textOverflow',
  textUnderlineOffset: 'textUnderlineOffset',
  textDecorationThickness: 'textDecorationThickness',
  textDecorationStyle: 'textDecorationStyle',
  textDecorationColor: 'textDecorationColor',
  textDecorationSkipInk: 'textDecorationSkipInk',
  textUnderlinePosition: 'textUnderlinePosition',
  // Compound value
  textDecor: {
    textDecoration: 'textDecoration',
    textDecorationStyle: 'textDecorationStyle',
    textDecorationColor: 'textDecorationColor',
    textDecorationThickness: 'textDecorationThickness',
    textDecorationSkipInk: 'textDecorationSkipInk',
    textUnderlineOffset: 'textUnderlineOffset',
    textUnderlinePosition: 'textUnderlinePosition',
  },

  // Box
  backgroundClip: 'backgroundClip',
  backgrounds: 'backgroundImages',
  backgroundColor: 'backgroundColor',

  // NOTE: I strongly dislike the way I have implemented nested border
  // property resolution but I can't spend more time fighting it currently.
  // Need to figure out a less tedious way to handle this.

  // compound value
  border: {
    // compound value
    bottom: {
      color: 'borderBottomColor',
      style: 'borderBottomStyle',
      width: 'borderBottomWidth',
    },
    // compound value
    left: {
      color: 'borderLeftColor',
      style: 'borderLeftStyle',
      width: 'borderLeftWidth',
    },
    // compound value
    right: {
      color: 'borderRightColor',
      style: 'borderRightStyle',
      width: 'borderRightWidth',
    },
    // compound value
    top: {
      color: 'borderTopColor',
      style: 'borderTopStyle',
      width: 'borderTopWidth',
    },
  },
  borderColor: {
    borderBottomColor: 'borderBottomColor',
    borderLeftColor: 'borderLeftColor',
    borderRightColor: 'borderRightColor',
    borderTopColor: 'borderTopColor',
  },
  borderStyle: {
    borderBottomStyle: 'borderBottomStyle',
    borderLeftStyle: 'borderLeftStyle',
    borderRightStyle: 'borderRightStyle',
    borderTopStyle: 'borderTopStyle',
  },
  borderWidth: {
    borderBottomWidth: 'borderBottomWidth',
    borderLeftWidth: 'borderLeftWidth',
    borderRightWidth: 'borderRightWidth',
    borderTopWidth: 'borderTopWidth',
  },
  // compound value
  borderBottom: {
    color: 'borderBottomColor',
    style: 'borderBottomStyle',
    width: 'borderBottomWidth',
  },
  borderBottomColor: 'borderBottomColor',
  borderBottomStyle: 'borderBottomStyle',
  borderBottomWidth: 'borderBottomWidth',
  // compound value
  borderLeft: {
    color: 'borderLeftColor',
    style: 'borderLeftStyle',
    width: 'borderLeftWidth',
  },
  borderLeftColor: 'borderLeftColor',
  borderLeftStyle: 'borderLeftStyle',
  borderLeftWidth: 'borderLeftWidth',
  // compound value
  borderRight: {
    color: 'borderRightColor',
    style: 'borderRightStyle',
    width: 'borderRightWidth',
  },
  borderRightColor: 'borderRightColor',
  borderRightStyle: 'borderRightStyle',
  borderRightWidth: 'borderRightWidth',
  // compound value
  borderTop: {
    color: 'borderTopColor',
    style: 'borderTopStyle',
    width: 'borderTopWidth',
  },
  borderTopColor: 'borderTopColor',
  borderTopStyle: 'borderTopStyle',
  borderTopWidth: 'borderTopWidth',
  // compound value
  borderRadius: {
    borderRadiusTopLeft: 'borderRadiusTopLeft',
    borderRadiusTopRight: 'borderRadiusTopRight',
    borderRadiusBottomLeft: 'borderRadiusBottomLeft',
    borderRadiusBottomRight: 'borderRadiusBottomRight',
    borderRadius: 'borderRadius',
  },
  borderRadiusTopLeft: 'borderRadiusTopLeft',
  borderRadiusTopRight: 'borderRadiusTopRight',
  borderRadiusBottomLeft: 'borderRadiusBottomLeft',
  borderRadiusBottomRight: 'borderRadiusBottomRight',
  boxShadows: 'boxShadows',

  // outline
  outlineColor: 'outlineColor',
  outlineOffset: 'outlineOffset',
  outlineStyle: 'outlineStyle',
  outlineWidth: 'outlineWidth',

  // Effects
  transforms: 'transforms',
  transitions: 'transitions',
  filters: 'filters',
  backdropFilters: 'backdropFilters',
  opacity: 'opacity',
  cursor: 'cursor',
  pointerEvents: 'pointerEvents',
  mixBlendMode: 'mixBlendMode',

  // Effects Transform Settings
  backface: 'backfaceVisibility',
  transformOrigin: 'transformOrigin',
  selfPerspective: 'transforms', // See issue: https://github.com/webflow/webflow/issues/24344
  childPerspectiveDistance: 'perspective',
  childPerspectiveOrigin: 'perspectiveOrigin',

  // compound value
  transformsAndSettings: {
    backface: 'backfaceVisibility',
    transformOrigin: 'transformOrigin',
    childPerspectiveDistance: 'perspective',
    childPerspectiveOrigin: 'perspectiveOrigin',
    transforms: 'transforms',
  },
} as const;

export const getCompoundPaths = (
  pathMap: CompoundStyleSystemPath
): Array<StyleSystemPath> => {
  let pathList: Array<StyleSystemPath> = [];
  for (const path of Object.values(pathMap)) {
    // check if compound path
    if (typeof path === 'string') {
      pathList.push(path);
    } else {
      pathList = pathList.concat(
        getCompoundPaths(path as CompoundStyleSystemPath)
      );
    }
  }
  return pathList;
};
